/* import __COLOCATED_TEMPLATE__ from './users-list-item.hbs'; */
import { A } from '@ember/array';
import { readOnly } from '@ember/object/computed';
import EmberObject, { computed } from '@ember/object';
import { inject as service } from '@ember/service';
import Component from '@ember/component';
import { USER } from '../models/user';
import { copyLinkToClipboard } from '../utils/url';
import { LSG_BADGE } from './lsg/lsg-badge';
import { ROLE } from '../models/role';
import { task } from 'ember-concurrency';
import { showModal } from "sh-www/utils/modal-controls";

const CLASS_NAME = 'SH-c-users-list-item';

export default Component.extend({
  tagName: '',
  CLASS_NAME,

  ROLE,
  LSG_BADGE,

  applicationStateManager: service('state-managers/application'),
  store: service(),

  /**
   * Component params
   */

  /** @type {String} */
  editedEmail: '',

  /** @type {Boolean} */
  editingEmail: false,

  /**
   * Tie to a task isRunning to disable actions during execution
   * @type {Boolean}
   */
  disabled: true,

  /** @type {Ember.Object} */
  menuOptions: EmberObject.create({}),

  /** @type {Task} */
  updateUserEmail: null,

  /** @type {User} */
  user: null,

  init() {
    this._super(...arguments);

    // this.get('_loadUserApplications').perform();
  },

  approvalText: computed(
    'user.approval',
    function () {
      switch (this.user.approval) {
        case USER.APPROVAL.UN_APPROVED:
          return 'Banned';
        case USER.APPROVAL.UNDEFINED:
        case USER.APPROVAL.APPROVED:
        default:
          return 'Approval';
      }
    }
  ),

  _badgeSelect: readOnly('user.select'),

  _badgeAdmin: readOnly('user.isAdmin'),

  _badgeCompanyAdmin: computed(
    'user.companyMembership.admin',
    function () {
      return this.user.companyMembership?.admin;
    }
  ),

  _badgeCompanyEditor: computed(
    'user.companyMembership.editor',
    function () {
      return this.user.companyMembership?.editor;
    }
  ),

  _badgeSuperAdmin: readOnly('user.superAdmin'),

  /** @type {Boolean} */
  _showAbsoluteTime: false,

  /** @type {Ember.MutableArray} */
  _userApplications: A([]),

  _loadUserApplications: task(function *() {
    const params = {
      'page': 1, // Only fetch one page of results
      'count': 100,
      'filter': {
        userId: this.get('user.id')
      }
    };

    const applications = yield this.get('store').query('application', params);

    this.set('_userApplications', applications);
  }),

  get isPublicText() {
    return this.user.isPublic ? "Public" : "Not Public";
  },

  _momentCalendarOptions: {
    sameDay: '[Today at] h:mma',
    nextDay: '[Tomorrow at] h:mma',
    lastDay: '[Yesterday at] h:mma',
    lastWeek: '[Last] dddd [at] h:mma',
    sameElse: 'D MMM YYYY [at] h:mma',
  },

  _userInviterCompanies: computed(
    'user.companySellerConnections.@each.{company,invited}',
    function () {
      const inviterCompanies = this.user.companySellerConnections.reduce((accum, companyConnection) => {
        if (companyConnection.invited) {
          accum.add(companyConnection.company);
        }

        return accum;
      }, new Set());

      return [...inviterCompanies].sort((a, b) => a.name.localeCompare(b.name));
    }
  ),

  _userLanguages: computed(
    'user.profile',
    'user.profile.languages.[]',
    function () {
      const profile = this.user.profile;
      if (!profile) {
        return '';
      }

      return this.user.profile.languages
        .sortBy('name')
        .map((language) => language.name)
        .join(' • ')
    }
  ),

  _userPayoutAccount: computed(
    'user.preferredPayoutAccount',
    function () {
      const payoutAccount = this.get('user.preferredPayoutAccount');

      return (payoutAccount && payoutAccount.get('id'))
        ? `${payoutAccount.get('accountTypeText')} (${payoutAccount.get('providerId')})`
        : '';
    }
  ),

  _userTypeString: computed(
    'user.type',
    function () {
      switch (this.get('user.type')) {
        case USER.TYPES.GUEST:
          return 'Guest';
        case USER.TYPES.SELLER:
          return 'Creator';
        case USER.TYPES.BUYER:
          return 'Buyer';
        case USER.TYPES.ADMIN:
          return 'Admin';
        default:
          return 'Unknown';
      }
    }
  ),

  /** @inheritDoc */
  actions: {
    editEmailInput(value) {
      this.set('editedEmail', value);
    },

    onCopyEmailClicked() {
      copyLinkToClipboard(this.get('user.email'));
      this.get('applicationStateManager').addToast('Email copied');
    },

    onCopyIdClicked() {
      copyLinkToClipboard(this.get('user.id'));
      this.get('applicationStateManager').addToast('User ID copied');
    },

    onCopyPhoneClicked() {
      copyLinkToClipboard(this.get('user.phone'));
      this.get('applicationStateManager').addToast('Phone number copied');
    },

    onCopyPublicSlugClicked() {
      copyLinkToClipboard(this.user.publicSlug);
      this.applicationStateManager.addToast('Public slug copied');
    },

    onEditEmailClicked() {
      if (this.disabled) {
        return;
      }

      this.set('editedEmail', this.user.email);
      this.toggleProperty('editingEmail');
    },

    onRandomizePublicSlugClicked() {
      if (this.disabled) {
        return;
      }

      showModal(this, 'confirmation', {
        isShowCancelButton: true,
        titleText: 'Generate New Public Slug',
        bodyText: 'WARNING! This will generate a new random public slug for this user, which could be disruptive as it will change their public URL. Are you sure?',
        confirmAction: () => {
          this.randomizePublicSlug(this.user);
        },
      });
    },

    toggleShowAbsoluteTime() {
      this.toggleProperty('_showAbsoluteTime');
    },

    updateEmail() {
      if (this.disabled) {
        return;
      }

      this.updateUserEmail(this.user, this.editedEmail);
      this.set('editingEmail', false);
    },
  },
});
